import { padSeriesToPeriod } from '@/modules/analytics/utils/padSeriesToPeriod';
import { CampaignDailyPtpPoint } from '@/shared/types/campaigns';

export interface CumulativePoint {
  date: string;
  value: number;
}

/**
 * Turns the daily PTP buckets into a running total.
 *
 * Only days with a promise are returned, so missing days are zero-filled before
 * accumulating and the axis spans the whole period. A null amount contributes nothing
 * but does not reset the total, so the curve flattens rather than drops.
 *
 * `hasData` is false when every value is 0 — common for amounts, which many campaigns
 * never captured — so the caller can show an empty state instead of a flat zero line.
 */
export const toCumulativePtp = (
  points: CampaignDailyPtpPoint[] = [],
  field: 'ptp_count' | 'ptp_amount'
): { series: CumulativePoint[]; hasData: boolean } => {
  if (points.length === 0) {
    return { series: [], hasData: false };
  }

  const daily = points.map((point) => ({ date: point.date, value: point[field] ?? 0 }));
  const hasData = daily.some((point) => point.value > 0);

  const dates = daily.map((point) => point.date).sort();
  const padded = padSeriesToPeriod(
    daily,
    { start: dates[0], end: dates[dates.length - 1] },
    'zero'
  );

  let total = 0;
  return {
    series: padded.map((point) => {
      total += point.value ?? 0;
      return { date: point.date, value: total };
    }),
    hasData,
  };
};
